import JSZip from 'jszip';

export type ImageZipEntry = string | { url: string; filename?: string };

export type DownloadImageZipResult = {
  zipCount: number;
  fileCount: number;
};

/** 한 ZIP에 넣을 최대 이미지 수. 넘으면 `_1`, `_2`로 나눠 받는다. */
export const IMAGE_ZIP_CHUNK_SIZE = 30;

const ZIP_FETCH_CONCURRENCY = 4;
const ZIP_PART_DELAY_MS = 500;

const normalizeZipEntries = (
  items: ImageZipEntry[],
): Array<{ url: string; filename?: string }> => {
  const seen = new Set<string>();
  const entries: Array<{ url: string; filename?: string }> = [];

  for (const item of items) {
    const url = (typeof item === 'string' ? item : item.url).trim();
    if (!url) continue;
    const filename =
      typeof item === 'string' ? undefined : item.filename?.trim() || undefined;
    const key = `${url}::${filename ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({ url, filename });
  }

  return entries;
};

export const chunkImageZipEntries = <T>(items: T[], size: number): T[][] => {
  const chunkSize = Math.max(1, size);
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
};

const fallbackFileName = (url: string, index: number): string => {
  try {
    const path = new URL(url).pathname.split('/').pop();
    if (path) return decodeURIComponent(path);
  } catch {
    // 상대 경로·잘못된 URL이면 아래에서 인덱스 이름을 쓴다
  }
  return `image-${index}`;
};

const uniquify = (desired: string, usedNames: Set<string>): string => {
  if (!usedNames.has(desired)) {
    usedNames.add(desired);
    return desired;
  }
  const extMatch = desired.match(/(\.[^.]+)$/);
  const ext = extMatch?.[1] ?? '';
  const base = ext ? desired.slice(0, -ext.length) : desired;
  let index = 2;
  let candidate = `${base}_${index}${ext}`;
  while (usedNames.has(candidate)) {
    index += 1;
    candidate = `${base}_${index}${ext}`;
  }
  usedNames.add(candidate);
  return candidate;
};

const mapPool = async <T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> => {
  let next = 0;
  const runners = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (next < items.length) {
        const current = next;
        next += 1;
        await worker(items[current], current);
      }
    },
  );
  await Promise.all(runners);
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * 원격 파일을 blob으로 받는다.
 * S3 presigned URL은 쿼리 서명까지 그대로 보내야 한다.
 */
export const fetchRemoteBlob = async (uri: string): Promise<Blob> => {
  const response = await fetch(uri, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`다운로드 실패 (${response.status})`);
  }
  return response.blob();
};

const triggerBlobDownload = (blob: Blob, downloadName: string) => {
  const zipUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = downloadName;
  link.href = zipUrl;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(zipUrl);
};

const buildPartFileName = (
  fileName: string,
  partIndex: number,
  totalParts: number,
) => (totalParts <= 1 ? fileName : `${fileName}_${partIndex + 1}`);

/**
 * 이미지 URL 배열을 ZIP으로 받는다.
 * 장수가 많으면 여러 개로 나눠 `_1.zip`, `_2.zip`처럼 저장한다.
 */
export const downloadImageZip = async (
  uriArray: ImageZipEntry[],
  fileName: string,
  options?: { chunkSize?: number; partDelayMs?: number },
): Promise<DownloadImageZipResult> => {
  const entries = normalizeZipEntries(uriArray);

  if (entries.length === 0) {
    throw new Error('다운로드할 이미지가 없습니다.');
  }

  const chunkSize = options?.chunkSize ?? IMAGE_ZIP_CHUNK_SIZE;
  const partDelayMs = options?.partDelayMs ?? ZIP_PART_DELAY_MS;
  const chunks = chunkImageZipEntries(entries, chunkSize);
  let zipCount = 0;
  let fileCount = 0;

  for (let partIndex = 0; partIndex < chunks.length; partIndex += 1) {
    const chunk = chunks[partIndex];
    const downloaded: Array<{ filename: string; blob: Blob }> = [];
    const indexOffset = partIndex * chunkSize;

    await mapPool(
      chunk,
      ZIP_FETCH_CONCURRENCY,
      async ({ url, filename }, index) => {
        try {
          const blob = await fetchRemoteBlob(url);
          downloaded.push({
            filename: filename || fallbackFileName(url, indexOffset + index),
            blob,
          });
        } catch (error) {
          console.warn(`이미지 다운로드 실패: ${url}`, error);
        }
      },
    );

    if (downloaded.length === 0) {
      continue;
    }

    const zip = new JSZip();
    const usedNames = new Set<string>();
    for (const item of downloaded) {
      zip.file(uniquify(item.filename, usedNames), item.blob);
    }

    // jpeg/png는 이미 압축돼 있어 DEFLATE는 메모리만 늘린다
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE',
    });
    triggerBlobDownload(
      zipBlob,
      `${buildPartFileName(fileName, partIndex, chunks.length)}.zip`,
    );
    zipCount += 1;
    fileCount += downloaded.length;

    if (partIndex < chunks.length - 1 && partDelayMs > 0) {
      await sleep(partDelayMs);
    }
  }

  if (zipCount === 0) {
    throw new Error('이미지를 받지 못했습니다.');
  }

  return { zipCount, fileCount };
};
